import React, { useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { Card, CardHeader, CardContent, CardActions, Button, Divider, Tooltip, IconButton, lighten, CircularProgress } from '@mui/material';

import { CardBlock, CardBlockHeader, CardBlockContainer, CardSpacer } from '../../../utils/utilities/CardComponents';

import BooleanChip from '../../../utils/utilities/BooleanChip';

import SimpleChip from '../../../utils/utilities/SimpleChip';

import { theme } from '../../../utils/theme';

import WigIcon from '../../../images/WigIconWrapper';

// mui icons right arrow for next stage
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded';
import NextPlanRoundedIcon from '@mui/icons-material/NextPlanRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import UnarchiveRoundedIcon from '@mui/icons-material/UnarchiveRounded';
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import PermContactCalendarRoundedIcon from '@mui/icons-material/PermContactCalendarRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';

// print imports
import LocalPrintshopRoundedIcon from '@mui/icons-material/LocalPrintshopRounded';
import FileDownloader from '../../../utils/FileDownloader';

import { BasicFlex, Notes, InputLabel } from '../../../utils/Common';

import { relativeTime, formatGenericDate } from '../../../utils/util';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateStage, checkOutWig } from '../apiService';

import useSnackbarStore from '../../../store/snackbar';

// comment
import CommentSection from '../../../utils/utilities/Comments';

// timeline
import TimelineComponent from '../../../utils/utilities/Timeline';

import TimelineWithComments from '../../../utils/utilities/Timeline/TimelineWithComments';

const PBContainer = styled(BasicFlex)`
  justify-content: flex-start;
  flex-direction: row;
  gap: .5rem;
  margin: .5rem 0;
`
const PBTrack = styled.div`
  height: .8rem;
  border-radius: 5px;
  background-color: ${props => props.theme.colors.darkBg};
  overflow: hidden;
  flex-grow: 1;
  /* width: 100%; */
`
const PBFill = styled.div`
  height: 100%;
  background-color: green;
  width: ${props => props.$width || "0"};
`

const PBProgress = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 2.5rem;
  font-size: 1.2rem;
`

const TicketTimeline = styled.div`
  margin: 2rem 0;
`;

const Actions = styled(BasicFlex)`
  width: auto;
  gap: .5rem;
`

const ActionChipStyle = styled(BasicFlex)`
  width: fit-content;
  padding: .3rem .7rem;
  border-radius: 5px;
  background-color: ${props => props.$color || props.theme.colors.lightBg};
  font-size: 1.2rem;
  font-weight: 500;
  color: ${props => props.theme.colors.mildText};
  gap: .3rem;
  cursor: pointer;

`

const ProgressBar = ({stage, numOfstages}) => {
  let progress = (stage / numOfstages) * 100
  return (
    <PBContainer>
      <PBTrack>
        <PBFill $width={`${progress}%`} />
      </PBTrack>
      <PBProgress>{stage}/{numOfstages}</PBProgress>
    </PBContainer>
  )
}

const KanbanTicket = ({ ticket = {}, receive = false, onClick = () => {}, expanded = false}) => {
  const [expand, setExpand] = useState(expanded || false);

  const [printLoading, setPrintLoading] = useState(false);

  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  const queryClient = useQueryClient();

  const { mutate: updateStageMutation } = useMutation({
    mutationFn: updateStage,
    onSuccess: () => {
      showSnackbar('Updated successfully!', 'success');
      queryClient.invalidateQueries({ queryKey: ['service_tickets'] });
    }
  })

  const { mutate: check_out_wig, isPending } = useMutation({
    mutationFn: checkOutWig,
    onSuccess: () => {
      showSnackbar('Updated successfully!', 'success');
      queryClient.invalidateQueries({ queryKey: ['service_tickets'] });
    }
  })

  const downloadFile = async () => {
    let fileDownloader = new FileDownloader();
    setPrintLoading(true);
    try {
      await fileDownloader.downloadAndDisplayPdf(`service_tickets/generate/slip/${ticket.service_ticket_id}`, "Service Ticket", "", "application/pdf");
    } catch (e) {
      console.log(e);
    } finally {
      setPrintLoading(false);
    }
  }

  let curStation = ticket?.prep_stages?.findIndex(stage => stage.name === ticket.current_station);
  // curStation = curStation === -1 ? 0 : curStation
  let stages = ticket?.prep_stages
  let status = curStation === -1 ? "completed" : (stages?.[curStation]?.status || "pending")
  status = ticket.status === "in_progress" ? "pending" : status
  let nextStage = stages?.[curStation + 1]?.name
  let curStep = curStation === -1 ? 0 : status === "pending" ? curStation : curStation + 1;
  if (ticket.status === "in_progress") {
    nextStage = ""
    curStep = ticket.prep_stages.length
  } else if (ticket.status === "prep_done") {
    nextStage = "Appointment"
    curStep = ticket.prep_stages.length
  }
  let events = stages.map(stage => {
    return {
      agent: "Moshe",
      date: stage.received_at,
      action: `#${ticket.wig_id} was received at ${stage.name}`
    }
  })

  events.push({
    agent: "Moshe",
    date: ticket.checked_in_at,
    comment: `@Tanya please wash wig thoroughly`
  })
  const mainInfoBlock = [
    {
      header: "", icon: <CalendarMonthRoundedIcon fontSize='small' color="icon" />, info: formatGenericDate(ticket.scheduled_date), spread: "1rem", dataType: "text"
    },
    {
      header: "", icon: <PermContactCalendarRoundedIcon fontSize='small' color="icon" />, info: ticket.client_name, spread: "1rem", dataType: "text"
    },
    {
      header: "", icon: <SettingsSuggestRoundedIcon fontSize='small' color="icon" />, info: <SimpleChip label={ ticket.service_name}  />, spread: "1rem", dataType: "text"
    },
    // {
    //   header: "Received", icon: <HourglassBottomRoundedIcon fontSize='small' color="icon" />, info: stages?.[curStation]?.received_at ? relativeTime(stages?.[curStation]?.received_at) : relativeTime(ticket.checked_in_at), spread: "1rem"
    // },
    {
      header: "", icon: <NextPlanRoundedIcon fontSize='small' color="icon" />, info: nextStage, dataType: "text", spread: "1rem"
    },
  ]

  const additionalInfoBlock = [
    {
      header: "Checked in", icon: <TaskAltRoundedIcon fontSize='small' color="icon" />, info: formatGenericDate(ticket.checked_in_at), dateType: "date", spread: "1rem"
    }
  ]
  return (
    <Card elevation={0} sx={{width: "100%", backgroundColor: expanded ? lighten(theme.colors.primaryFaint, .8) : ""}}>

      <CardHeader 
        title={`Ticket #${ticket.service_ticket_num}`} 
        subheader={<BasicFlex justify="flex-start" gap=".5rem"><WigIcon dimensions='1.5rem' />{ticket.wig_id}</BasicFlex>} sx={{p: expanded ? 2 : 1}} 
        slotProps={{title: {sx: {fontSize: "1.4rem"}}, subheader: {sx: {fontSize: "1.2rem", fontWeight: "500"}}}} 
        action={!expanded && (
          <BasicFlex gap=".5rem">
            <BooleanChip check={status === "completed"} values={["Ready", "Pending"]} />
            <Tooltip title="Expand">
              <IconButton onClick={() => setExpand(ex => !ex)}><MoreVertRoundedIcon fontSize='small' /></IconButton>
            </Tooltip>
          </BasicFlex>
        )} 
        />

      <Divider />

      <CardContent sx={{p: expanded ? 2 : 1, pt: 1}}>
        <CardBlockContainer $gap=".7rem">
          {mainInfoBlock.map((block, index) => (
            <CardBlock key={index} {...block} />
          ))}
          {expand && <>
          <CardSpacer />
          <CardBlockHeader>Additional Info</CardBlockHeader>
          </>}
          {expand && additionalInfoBlock.map((block, index) => (
            <CardBlock key={index} {...block} />
          ))}
        </CardBlockContainer>
        {expand && ticket?.notes && <>
          <CardSpacer />

          <InputLabel>Notes</InputLabel>
          <Notes>
            {ticket?.notes}
          </Notes></> }

        {expand && (
          <TicketTimeline>
            <TimelineWithComments title="Service Ticket History" resource="service_tickets" resource_id={ticket.service_ticket_id} />
          </TicketTimeline>
        )} 
        <ProgressBar stage={curStep} numOfstages={ticket?.prep_stages?.length} />
      </CardContent>
      <Divider />
      <CardActions sx={{p: 1, pt: expanded ? 1 : ".2rem", justifyContent: "space-between"}}>
        <Actions>
          <Tooltip title="Print Ticket">
            <ActionChipStyle onClick={downloadFile}>
              {printLoading ? <CircularProgress size={20} /> : <LocalPrintshopRoundedIcon fontSize='small' color="tertiary" />}
            </ActionChipStyle>
          </Tooltip>
        </Actions>
        <Actions>
          {/* <Button size="small" variant="outlined">Mark Ready</Button> */}
          {!receive ? <BasicFlex width="auto" gap="1rem">
            {ticket?.status === "checked_in" ? <Button size="small" startIcon={ <UndoRoundedIcon />} loading={isPending} sx={{fontSize: "1.2rem"}} color="warning" onClick={(e) => {
              check_out_wig({service_ticket_id: ticket.service_ticket_id});
            }}>Undo</Button> : null}
            {ticket?.status === "checked_in" && <Divider orientation="vertical" flexItem /> }
            <Button size="small" endIcon={<ArrowRightAltRoundedIcon />} sx={{fontSize: "1.2rem"}} onClick={(e) => updateStageMutation({service_ticket_id: ticket.service_ticket_id, step: status === "completed" ? "proceed" : "complete"})}>{status === "completed" ? "Proceed" : "Complete"}</Button>
          </BasicFlex> :
          <Button size="small" endIcon={<UnarchiveRoundedIcon />} variant={expanded ? "contained" : "text"} sx={{fontSize: "1.2rem"}} onClick={(e) => onClick(e, ticket)}>Receive</Button> }
        </Actions>
      </CardActions>
    </Card>
  )
};

export default KanbanTicket;